/**
 * Sanitizes HTML content to prevent XSS attacks
 * Uses DOMPurify-like approach with basic tag whitelisting
 * 
 * SECURITY: This is a basic sanitizer. For production use, consider using DOMPurify library
 * which provides more robust XSS protection.
 */
export function sanitizeHtml(dirty: string): string {
  if (!dirty) return ''

  // Create a temporary div element
  const temp = document.createElement('div')
  temp.textContent = dirty
  return temp.innerHTML
}

/**
 * Sanitizes HTML but allows safe formatting tags (bold, italic, links)
 * Use with caution - only for trusted content sources (e.g., localization strings)
 * 
 * SECURITY: This allows specific safe HTML tags. Only use for content from trusted sources.
 * For user-generated content, use sanitizeHtml() instead.
 */
export function sanitizeHtmlWithFormatting(dirty: string): string {
  if (!dirty) return ''

  // Create a temporary div to parse HTML
  const temp = document.createElement('div')
  temp.innerHTML = dirty

  // List of allowed tags and their allowed attributes
  const allowedTags = ['b', 'strong', 'i', 'em', 'u', 'br', 'p', 'a', 'span']
  const allowedAttributes: Record<string, string[]> = {
    a: ['href', 'target', 'rel', 'style'],
    span: ['style']
  }

  // Recursively sanitize the DOM tree
  const sanitizeNode = (node: Node): void => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element
      const tagName = element.tagName.toLowerCase()

      // If tag is not allowed, replace with text content
      if (!allowedTags.includes(tagName)) {
        const textNode = document.createTextNode(element.textContent || '')
        element.parentNode?.replaceChild(textNode, element)
        return
      }

      // Remove disallowed attributes
      const allowedAttrs = allowedAttributes[tagName] || []
      Array.from(element.attributes).forEach(attr => {
        if (!allowedAttrs.includes(attr.name.toLowerCase())) {
          element.removeAttribute(attr.name)
        }
      })

      // Sanitize href attribute for links
      if (tagName === 'a' && element.hasAttribute('href')) {
        const href = element.getAttribute('href') || ''
        // Only allow http, https, and relative URLs
        if (!href.match(/^(https?:\/\/|\/|#|mailto:)/i)) {
          element.removeAttribute('href')
        }
        // Ensure rel="noopener noreferrer" for external links
        if (href.startsWith('http')) {
          element.setAttribute('rel', 'noopener noreferrer')
          element.setAttribute('target', '_blank')
        }
      }

      // Recursively sanitize child nodes
      Array.from(element.childNodes).forEach(child => sanitizeNode(child))
    } else if (node.nodeType === Node.TEXT_NODE) {
      // Text nodes are safe
      return
    }
  }

  // Sanitize all nodes
  Array.from(temp.childNodes).forEach(node => sanitizeNode(node))

  return temp.innerHTML
}

/**
 * Safely renders HTML content by escaping it
 * Use this instead of dangerouslySetInnerHTML when possible
 */
export function safeHtml(html: string): { __html: string } {
  return { __html: sanitizeHtml(html) }
}
